import { combineEpics, ofType } from 'redux-observable';
import { Observable, of, race } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  filter,
  groupBy,
  map,
  mergeMap,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';
import AppState from '../reducers/app-state';
import { requestActions } from './apiHelpers';
import { ReduxAction } from '@repo/utils/types';
import { DisplayLocation } from '@repo/utils/models/SystemAlert';
import { apiObservable } from '@repo/utils/helpers/api';

export type AlertType = 'loading' | 'success' | 'error';

interface ActionParts {
  method?: string;
  resourceType?: string;
  requestState?: string;
}

/* Epic actions */
const systemAlertActions = requestActions('SYSTEM_ALERT', ['fetch']);

/* Component actions */
export const alertUser = (text: string, type?: AlertType) => ({
  type: 'ALERT_USER',
  payload: { text, type },
});
export const clearAlerts = () => ({ type: 'CLEAR_ALERTS' });
export const fetchSystemAlert = systemAlertActions['fetch'];
export const dismissSystemAlert = () => ({ type: 'DISMISS_SYSTEM_ALERT' });

/* Selectors */
export const selectAlert = (state: AppState) => state.ui.alerts.message;
const selectSystemAlertFor = (location: DisplayLocation) => (state: AppState) => {
  const alert = state.ui.alerts.systemAlert;

  if (alert && alert.displayIn.includes(location)) {
    return alert;
  }

  return null;
};
export const selectBannerSystemAlert = selectSystemAlertFor('banner');
export const selectUdoMenuSystemAlert = selectSystemAlertFor('menu');

// Epics
const isAutoAlertableAction: (actionParts: ActionParts) => boolean = ({
  method,
  resourceType,
  requestState,
}) => {
  return method && resourceType && requestState == 'REQUESTED';
};

const alertableResource = (resource: string): string | null => {
  if (/(SYSTEM_ALERT|MOMENTS|RECONNECT_CADENCE|STARRED|GROUP_ORDER)/.test(resource)) {
    return null;
  }

  return resource;
};

const actionMatcher: (action: ReduxAction) => ActionParts = (action) => {
  const [_, method, resource, requestState] =
    action.type.match(/(FETCH_ALL|FETCH|CREATE|UPDATE|DELETE)_(.*)_(REQUESTED|SUCCEEDED|FAILED)/) ||
    [];
  const resourceType = alertableResource(resource);

  return { method, resourceType, requestState };
};

const toAlertActionOfType = (type: string) => (payload: ActionParts) => ({ type, payload });

// All actions that pass the filter result in an API_REQUEST
const autoAlertableRequestsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    map(actionMatcher),
    filter(isAutoAlertableAction),
    map(toAlertActionOfType('API_REQUEST')),
  );

/*
After an API_REQUEST action, we have three possible paths
	1. If request succeeds before 1000ms, nothing happens (handled by our debounceTime)
	2. If request fails before 1000ms, we dispatch a *_FAILED action, which results in a failure
		 message being rendered in our alert toast.
	3. If request hasn't resolved in a 1000ms, we dispatch a *_REQUESTED action, which
		 results in the spinner being rendered in the alert toast. The loadingResolutionAlertsEpic
		 handles updating alerting in response to the eventual success or failure of this request.
*/
const loadingStateAlertsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('API_REQUEST'),
    groupBy(({ payload }) => `${payload.method}_${payload.resourceType}`),
    mergeMap((requestsByMethodAndResource) => {
      const { key } = requestsByMethodAndResource;
      return race(
        requestsByMethodAndResource.pipe(
          filter(({ payload }) => payload.requestState == 'REQUESTED'),
          debounceTime(1000),
          map(({ payload }) => ({ type: '*_REQUESTED', payload })),
          takeUntil(action$.pipe(ofType(`${key}_SUCCEEDED`, `${key}_FAILED`))),
        ),
        action$.pipe(
          ofType(`${key}_FAILED`),
          map(actionMatcher),
          map(toAlertActionOfType('*_FAILED')),
          take(1),
        ),
      );
    }),
  );

// Once we've exceeded 1000ms and the UI shows a loading state (spinner in toast), we
// wait for the related success or failure action to clear that state
const loadingResolutionAlertsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('*_REQUESTED'),
    mergeMap(({ payload }) => {
      const key = `${payload.method}_${payload.resourceType}`;
      return action$.pipe(
        ofType(`${key}_SUCCEEDED`, `${key}_FAILED`),
        map(actionMatcher),
        map((payload) => toAlertActionOfType(`*_${payload.requestState}`)(payload)),
        take(1),
      );
    }),
  );

// After success, failure, and arbitrary alert actions, we automatically clear alerts after
// 1.5 seconds.
export const autoClearAlertsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('*_SUCCEEDED', '*_FAILED', 'ALERT_USER'),
    delay(1500),
    mergeMap(() => of(null).pipe(map(clearAlerts))),
  );

// @ts-ignore
const statusJsonURL = STATUS_JSON_URL;

export const fetchSystemAlertEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_SYSTEM_ALERT'),
    mergeMap(() =>
      apiObservable(
        'GET',
        statusJsonURL,
        {},
        {
          noAuthorization: true,
          noTimeZone: true,
          timeStamp: true,
          isFullPath: true,
          headers: {
            Accept: '*/*',
          },
        },
      ).pipe(
        map(systemAlertActions['fetchSucceeded']),
        startWith(systemAlertActions['fetchRequested']()),
        catchError((err) => of(systemAlertActions['fetchFailed'](err))),
      ),
    ),
  );

export const alertsEpic = combineEpics<ReduxAction, any, AppState>(
  autoAlertableRequestsEpic,
  loadingStateAlertsEpic,
  loadingResolutionAlertsEpic,
  autoClearAlertsEpic,
  fetchSystemAlertEpic,
);
